<!-- eslint-disable max-len -->
<template>
  <div class="fact-sheet">
    <Stack class="main-stack">
      <Stack gap="4">
        <Level>
          <LevelLeft>
            <Title resize="6">
              {{ client.name }}
            </Title>
          </LevelLeft>
          <LevelRight>
            <Buttons>
              <Badge status="info">
                Date Generated: {{ client.dateGenerated }}
              </Badge>
              <Button
                class="no-print"
                icon-before="fas fa-arrow-left"
                spacing="tight"
                :to="{ name: 'entity-detail', params: { entityExternalId: entityExternalId } }"
              >
                Return
              </Button>
              <Button
                class="no-print"
                icon-before="fas fa-print"
                spacing="tight"
                @click="printPage"
              >
                Print
              </Button>
              <Button
                class="no-print"
                icon-before="fas fa-sync"
                spacing="tight"
                :loading="isRegenerating"
                @click="regenerateFactSheet"
              >
                Regenerate
              </Button>
            </Buttons>
          </LevelRight>
        </Level>

        <p class="sheet-summary">
          {{ client.summary }}
        </p>
      </Stack>

      <Callout
        v-if="!isLoading && isEmptyValue(client)"
        icon-before="fas fa-circle-notch"
        status="primary"
        title="This fact sheet is empty"
        bold
        spacing="loose"
      >
        It requires more information from the Dossier. Simply fill out fields and this fact sheet will be updated.
        <template v-slot:after>
          <Button
            :to="{ name: 'entity-detail', params: { entityExternalId: entityExternalId } }"
            icon-after="fas fa-arrow-right"
          >
            Go to the Dossier
          </Button>
        </template>
      </Callout>

      <KeyInfos
        :client="client"
        :loading="isLoading"
        :entity-external-id="entityExternalId"
      />

      <Recommendations
        v-if="riskRecommendations?.length"
        :recommendations="riskRecommendations"
      />

      <KeySubInfos
        :client="client"
        :loading="isLoading"
      />

      <div class="boxes">
        <PersonalInformation
          :client="client"
          :loading="isLoading"
          :entity-external-id="entityExternalId"
        />
        <IncomeExpenses
          :client="client"
          :loading="isLoading"
          :recommendations="getInlineRecommendationsForSection('Income & Expenses')"
          :entity-external-id="entityExternalId"
        />
        <PriorityRecommendations
          :client="client"
          :loading="isLoading"
        />
      </div>

      <AssetsLiabilities
        :client="client"
        :loading="isLoading"
        :recommendations="getInlineRecommendationsForSection('Assets & Liabilities')"
        :entity-external-id="entityExternalId"
      />

      <EstatePlanning
        :client="client"
        :loading="isLoading"
        :recommendations="getInlineRecommendationsForSection('Estate Planning')"
      />

      <div class="boxes">
        <AccountsInvestments
          :client="client"
          :loading="isLoading"
          :recommendations="getInlineRecommendationsForSection('Accounts')"
        />

        <Insurance
          :client="client"
          :loading="isLoading"
          :recommendations="getInlineRecommendationsForSection('Insurance')"
        />
      </div>
    </Stack>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { Badge, Buttons, Button, Callout, Level, LevelLeft, LevelRight, Stack, Title } from '@stakeui';
import { awaitTask } from '@frontend/ui/features/checkup/helpers/tasks';
import Recommendations from './Recommendations.vue';
import KeyInfos from './KeyInfos.vue';
import KeySubInfos from './KeySubInfos.vue';
import PersonalInformation from './PersonalInformation.vue';
import IncomeExpenses from './IncomeExpenses.vue';
import PriorityRecommendations from './PriorityRecommendations.vue';
import AssetsLiabilities from './AssetsLiabilities.vue';
import EstatePlanning from './EstatePlanning.vue';
import AccountsInvestments from './AccountsInvestments.vue';
import Insurance from './Insurance.vue';
import { isEmptyValue } from '@frontend/shared/Util.js';

const props = defineProps({
  entityExternalId: {
    type: String,
    required: true,
  },
});
const client = ref({});
const entity = ref(null);
const isLoading = ref(false);
const isRegenerating = ref(false);

const fetchEntity = async () => {
  const entityResponse = await fetch(`/api/v1/entities/${props.entityExternalId}/`);
  const entityData = await entityResponse.json();
  entity.value = entityData;
};

const fetchFactSheetData = async () => {
  isLoading.value = true;
  await fetchEntity();

  // Check if there's a latest fact sheet in the entity data
  const latestFactSheet = entity.value?.latest_fact_sheet;

  if (latestFactSheet) {
    latestFactSheet.recommendations = entity.value?.latest_recommendations || [];

    client.value = latestFactSheet;
    isLoading.value = false;
    return;
  }

  // If no fact sheet exists, request one from the API
  const data = await awaitTask(`/api/v1/entities/${props.entityExternalId}/fact-sheet/`, {
    method: 'GET',
    headers: {
      'X-CSRFToken': document.querySelector('[name="csrfmiddlewaretoken"]').value,
      'Content-Type': 'application/json',
    },
  });

  const factSheet = data?.task?.meta?.fact_sheet;

  if (factSheet) {
    client.value = factSheet;
  } else {
    alert('Failed to retrieve fact sheet data');
  }

  isLoading.value = false;
};

const regenerateFactSheet = async () => {
  isRegenerating.value = true;
  try {
    const response = await fetch(`/api/v1/entities/${props.entityExternalId}/fact-sheet/regenerate/`, {
      method: 'POST',
      headers: {
        'X-CSRFToken': document.querySelector('[name="csrfmiddlewaretoken"]').value,
        'Content-Type': 'application/json',
      },
    });
    const data = await response.json();
    if (data.status === 'success' && data.fact_sheet) {
      client.value = data.fact_sheet;
    } else {
      console.error('Failed to regenerate fact sheet:', data.message);
    }
  } catch (error) {
    console.error('Error regenerating fact sheet:', error);
  } finally {
    isRegenerating.value = false;
  }
};

const getInlineRecommendationsForSection = section => {
  if (!client.value?.inline_recommendations) return [];
  return client.value.inline_recommendations.filter(
      rec => rec.section?.toLowerCase() === section.toLowerCase(),
  );
};

const riskRecommendations = computed(() => ([...getInlineRecommendationsForSection('Risk Profile'), ...getInlineRecommendationsForSection('Goals')]));

const printPage = () => {
  window.print();
};

onMounted(() => {
  fetchFactSheetData();
});
</script>

<style scoped>
.fact-sheet {
  padding: 2rem;
}

.sheet-summary {
  strong {
    color: var(--stk-text-strong);
    font-weight: 600;
  }
}

.boxes {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(20rem, 1fr));
  gap: 1rem;
}

@media print {
  .fact-sheet {
    font-size: 0.625em;
    padding: 0;
  }

  .main-stack,
  .boxes {
    gap: 0.5rem !important;
  }

  .no-print {
    display: none;
  }
}
</style>

<style>
@media print {
  .ui-default-view {
    display: block !important;
  }

  .sidenav,
  .footer {
    display: none !important;
  }
}
</style>
